import { createFeatureSelector, createSelector, createSelectorFactory } from '@ngrx/store';
import * as fromStore from '../reducers/pigsig.reducer';
import { menuDataType, PigSigWallThickness } from 'src/app/models';

const pigsigState = createFeatureSelector<fromStore.State>(fromStore.pigsigFeatureKey);

export const selectLookups = createSelector(pigsigState, state => state.lookups);
export const selectIndicators = createSelector(selectLookups, lookups => lookups.indicators);
export const selectConnections = createSelector(selectLookups, lookups => lookups.connections);
export const selectShafts = createSelector(selectLookups, lookups => lookups.shafts);
export const selectDiameters = createSelector(selectLookups, lookups => lookups.diameters);
export const selectPlugs = createSelector(selectLookups, lookups => lookups.plugs);
export const selectOrings = createSelector(selectLookups, lookups => lookups.orings);
export const selectWallThicknesses = createSelector(selectLookups, lookups => lookups.wallThicknesses);
export const selectFlangeMaterials = createSelector(selectLookups, lookups => lookups.flangeMatls);
export const selectFlangeDynamicFlangeHeights = createSelector(pigsigState, state => state.activeFlangeHeights);

export const selectActiveFilter = createSelector(pigsigState, state => state.activeFilter);
export const selectActiveIndicator = createSelector(selectActiveFilter, fil => fil.indicatorID);
export const selectActiveIndicatorObj = createSelector(selectIndicators, selectActiveIndicator, (indicators, active) => indicators.find(i => i.id === active));
export const selectActiveDiameter = createSelector(selectActiveFilter, selectDiameters, (fil, diameters) => diameters.find(d => d.id === fil.diameterID));
export const selectActiveConnection = createSelector(selectActiveFilter, selectConnections, (fil, connections) => connections.find(c => c.id === fil.connectionID));
export const selectActiveShaft = createSelector(selectActiveFilter, selectShafts, (fil, shafts) => shafts.find(s => s.id === fil.shaftID));
export const selectActivePlug = createSelector(selectActiveFilter, selectPlugs, (fil, plugs) => plugs.find(p => p.id === fil.plugID));
export const selectActiveOring = createSelector(selectActiveFilter, selectOrings, (fil, orings) => orings.find(o => o.id === fil.oringID));
export const selectActiveWallThickness = createSelector(selectActiveFilter, selectWallThicknesses, (fil, walls) => walls.find(w => w.id === fil.wallThicknessID));
export const selectActiveFlangeMatl = createSelector(selectActiveFilter, selectFlangeMaterials, (fil, mats) => mats.find(w => w.id === fil.flangeMatlID));
export const selectActiveFlangeHeight = createSelector(selectActiveFilter, fil => fil.flangeHeight);
export const selectActiveFlangeMainlineSize = createSelector(selectActiveFilter, fil => fil.flangeConnLTEFour || false)
export const selectActiveNoTORValue = createSelector(selectActiveFilter, fil => fil.noTOR || false);

export const selectActiveFlangeHeightObj = createSelector(selectActiveFlangeHeight, selectFlangeDynamicFlangeHeights,
  (height, dynamicFlangeHeights) => dynamicFlangeHeights.filter(h => h.minHeight <= parseFloat((height || -1).toFixed(3)) && h.maxHeight >= parseFloat((height || -1).toFixed(3))));

export const selectActivePartNumber = createSelector(pigsigState, state => state.activePartNumber);
// the diameter control filters down the wall thicknesses
export const selectAvailableWallThicknesses = createSelector(selectActiveDiameter, selectWallThicknesses, (diameter, wallThicknesses) => wallThicknesses.filter(w => w.diameterId === diameter?.id));

export const selectFilterResults = createSelector(pigsigState, selectActiveFlangeMainlineSize, (state, flangemainlinesix) => flangemainlinesix ? undefined : state.results);


export const selectBuiltPartNumber = createSelector(
  selectActiveFilter,
  selectActiveDiameter,
  selectActiveConnection,
  selectActiveIndicatorObj,
  selectActiveShaft,
  selectActivePlug,
  selectActiveOring,
  selectActiveWallThickness,
  selectActiveFlangeMatl,
  selectActiveFlangeHeight,
  selectActiveFlangeHeightObj,
  (fil, selectedDiameter, selectedConnection, selectedIndicator, selectedShaft, selectedPlug, selectedOring, selectedWallThickness, selectedFlangMatl, selectedFlangeHeight, dynamicFlangeHeight) => {
    // TODO
    // 04-xxxx-xxxx-xxxx
    let res = '04-CONN-ISPR-WTXX';

    // This is for CONN
    if (fil.connectionID === fromStore.ConnectionType.TOR) {
      if (!!fil.hasEnclosure) {
        res = res.replace('CONN', '9552')
      } else {
        // if there is a diameter, take that
        if (!!fil.diameterID) res = res.replace('CONN', (selectedDiameter?.partNumberValue) || 'CONN')
      }

      // if it is a TOR, the plug should be the 'P'
      if (!!fil.plugID) res = res.replace('P', selectedPlug?.partNumberValue || 'P');

      // if it is does not include the TOR the suffix should be 99
      if (!!fil.noTOR) res = res.replace('WT', '99');

      // if it is a TOR, the XX should be blank
      res = res.replace('XX', '');
    } else {
      //this means connectionID is a flange
      res = res.replace('CONN', selectedConnection?.partNumberValue || 'CONN');
      if (!!fil.flangeConnLTEFour) res = res.replace('44', '45');

      // if it is a flange, the flange material should be the 'P'
      if (!!fil.flangeMatlID) res = res.replace('P', selectedFlangMatl?.partNumberValue || 'P');

    }

    if (!!fil.indicatorID) res = res.replace('I', selectedIndicator?.partNumberValue || 'I');
    if (!!fil.shaftID) res = res.replace('S', selectedShaft?.partNumberValue || 'S');

    if (!!fil.oringID) res = res.replace('R', selectedOring?.partNumberValue || 'R');
    if (!!fil.wallThicknessID) res = res.replace('WT', selectedWallThickness?.partNumberValue || 'WT');

    if (!!fil.flangeHeight && dynamicFlangeHeight.length === 1) res = res.replace('WTXX', dynamicFlangeHeight[0].partNumberValue);

    return res;
  });

export const selectBuiltPartNumberForDisplay = createSelector(selectBuiltPartNumber,
  pn => pn
    .replace('CONN', '****')
    .replace('I', '*')
    .replace('S', '*')
    .replace('P', '*')
    .replace('R', '*')
    .replace('WT', '**')
    .replace('XX', '**')
);

export const selectLoaders = createSelector(pigsigState, state => state.loaders);
export const selectIsLoading = (loader: fromStore.Loaders) => createSelector(selectLoaders, loaders => loaders.includes(loader));
export const selectAnyLoader = (qloaders: fromStore.Loaders[]) => createSelector(selectLoaders, loaders => qloaders.filter(l => loaders.includes(l)).length > 0)
